import styled, { StyledOptions } from '@emotion/styled';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import NavigationLinkUI from 'app/components/layout/AppLayout/NavigationLinkUI';
import AriaClickHandler from 'app/components/v2/components/AriaClickHandler';
import Box from 'app/components/v2/components/Box';
import styles from 'app/components/v2/constants/styles';
import { getSemanticColorVariable } from 'app/components/v2/theme/colors';

import { Link } from '.';

// Local Typings
interface Props extends Link {
  activePaths: string[];
  href?: string;
  isSideNavOpen: boolean;
}

// Local Variables
const navLinkStyles = {
  alignItems: 'center',
  border: `1px solid ${styles.backgrounds.clearWhite}`,
  cursor: 'pointer',
  display: 'flex',
  height: 48,
  padding: styles.padding.md,
  textDecoration: 'none',
  transition: '300ms',

  /* eslint-disable sort-keys,@typescript-eslint/naming-convention */
  '&:hover': {
    backgroundColor: getSemanticColorVariable('nav_item_hover'),
  },
  /* eslint-enable sort-keys,@typescript-eslint/naming-convention */
};

const styledOptions: StyledOptions = {
  shouldForwardProp(propName: string): boolean {
    return propName !== 'isSideNavOpen' && propName !== 'isActive';
  },
};

const StyledAnchor = styled('a', styledOptions)<{ isActive: boolean; isSideNavOpen: boolean }>(({
  isActive,
  isSideNavOpen,
  theme,
}) => ({
  ...navLinkStyles,
  backgroundColor: isActive ? getSemanticColorVariable('nav_item_active_background') : 'transparent',
  padding: isSideNavOpen ? `0 ${theme.spacing * 6}px` : `0 ${theme.spacing * 4}px`,
}));
const StyledNavLink = styled(NavLink, styledOptions)<{ isActive: boolean; isSideNavOpen: boolean }>(({
  isActive,
  isSideNavOpen,
  theme,
}) => ({
  ...navLinkStyles,
  backgroundColor: isActive ? getSemanticColorVariable('nav_item_active_background') : 'transparent',
  padding: isSideNavOpen ? `0 ${theme.spacing * 6}px` : `0 ${theme.spacing * 4}px`,
}));
const StyledSpan = styled('span', styledOptions)<{ isActive: boolean; isSideNavOpen: boolean }>(({
  isActive,
  isSideNavOpen,
  theme,
}) => ({
  ...navLinkStyles,
  backgroundColor: isActive ? getSemanticColorVariable('nav_item_active_background') : 'transparent',
  padding: isSideNavOpen ? `0 ${theme.spacing * 6}px` : `0 ${theme.spacing * 4}px`,
}));

// Component Definition
const NavigationLink: FC<Props> = props => {
  const { t } = useTranslation('components');
  const location = useLocation();

  if (!props.to && !props.onClick && !props.href) {
    return null;
  }

  const isActive = !!location && props.activePaths.some(path => location.pathname.includes(path));
  const className = isActive ? 'active' : '';

  const listItemId = `nav-item-${props.id}`;

  if (props.href) {
    return (
      <Box
        as="li"
        id={listItemId}
        marginY={1}
      >
        <StyledAnchor
          className={className}
          href={props.href}
          isActive={isActive}
          isSideNavOpen={props.isSideNavOpen}
        >
          <NavigationLinkUI
            hasNotifications={!!props.hasNotifications}
            IconProps={{
              color: isActive ? 'active' : 'utility',
              name: props.iconName,
            }}
            isActive={isActive}
            label={props.label}
          />
        </StyledAnchor>
      </Box>
    );
  }

  return (
    <Box
      as="li"
      id={listItemId}
      marginY={1}
    >
      {props.to
        ? (
          <StyledNavLink
            className={className}
            isActive={isActive}
            isSideNavOpen={props.isSideNavOpen}
            to={props.to}
          >
            <NavigationLinkUI
              hasNotifications={!!props.hasNotifications}
              IconProps={{
                color: isActive ? 'active' : 'utility',
                name: props.iconName,
              }}
              isActive={isActive}
              label={props.label}
            />
          </StyledNavLink>
        )
        : (
          <AriaClickHandler
            ariaLabel={t('layout.navigationLink.seeUpdates', 'See updates')}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            onClick={props.onClick!}
            role="button"
          >
            <StyledSpan
              className={className}
              isActive={isActive}
              isSideNavOpen={props.isSideNavOpen}
            >
              <NavigationLinkUI
                hasNotifications={!!props.hasNotifications}
                IconProps={{
                  color: isActive ? 'active' : 'utility',
                  name: props.iconName,
                }}
                isActive={isActive}
                label={props.label}
              />
            </StyledSpan>
          </AriaClickHandler>
        )}
    </Box>
  );
};

export default NavigationLink;
